import { Switch, useColorMode } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getArtistId, getMemberIdAnyway } from '@pocapan/shared/utils/etcUtils';
import saveData from '@pocapan/shared/utils/saveData';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import usePhotoCardEventsQuery from './hooks/usePhotoCardEventsQuery';
import useInvalidateQueries from './hooks/usePhotoCardQueryInvalidation ';
import useFilterPhotoCards from './hooks/useFilterPhotoCards';
import EmptyPhotoCardEventSkeletonV2 from '../../components/skeletons/EmptyPhotoCardEventSkeletonV2';
import EmptyCase from '../../components/errors/EmptyCase';
import PhotoCardsByEventViewer from './components/PhotoCardsByEventViewer';
import EmptyMyPhotoCardContent from './components/EmptyMyPhotoCardContent';
import PendingPhotoCardsSection from '../../components/photocard/PendingPhotoCardsSection';
import { ReportPhotoCardBox } from '../../components/report/ReportPhotoCardBox';
import HomeHeader from './components/HomeHeader';
import HomeBottomNav from '../components/bar/HomeBottomNav';
import ChangePhotoCardCollectedCountBottomSheet from '../../components/bottomSheet/ChangePhotoCardCollectedCountBottomSheet';
import LikedMembersList from '../../components/lists/LikedMembersList';
import useUserQuery from '../hooks/queries/useUserQuery';
import useRefreshJwtQuery from '../hooks/queries/useRefreshJwtQuery';
import useArtistQuery from '../hooks/queries/useArtistQuery';
import useReportedPhotoCardsQuery from '../hooks/queries/useReportedPhotoCardsQuery';
import ArtistIconSelector from '../../components/selectors/ArtistIconSelector';
import type { PhotoCardType } from '../types/PhotoCardType';
import type { ArtistType, MemberType } from '../types/ArtistType';
import HomeView from './components/HomeView';
import CollectionSummaryBar from './components/CollectionSummaryBar';

export default function CollectionsHomePage() {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const artistId = getArtistId();

  const [isShowChangeCollectCountBottomSheet, setIsShowChangeCollectCountBottomSheet] =
    useState(false);
  const [currentMember, setCurrentMember] = useState<MemberType | null>(null);
  const [selectedPhotoCard, setSelectedPhotoCard] = useState<PhotoCardType>();
  const [forceRender, setForceRender] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<ArtistType | null>(null);
  const [isMyPhotoCardsOnly, setIsMyPhotoCardsOnly] = useState(false);
  const [isHomeSelected, setIsHomeSelected] = useState(false);

  const effectiveArtistId = selectedArtist?.artistId || artistId || '';
  const currentMemberId = currentMember ? getMemberIdAnyway(currentMember) : '';

  // API 쿼리들
  useRefreshJwtQuery();
  const { data: user } = useUserQuery();
  const { data: artist } = useArtistQuery(artistId || '');
  const { data: reportedPhotoCards } = useReportedPhotoCardsQuery({
    user: user || null,
    artistId: effectiveArtistId,
    onlyPending: true
  });
  const { data, isLoading, hasNextPage, isFetchingNextPage, loadMore } = usePhotoCardEventsQuery({
    artistId: effectiveArtistId,
    memberId: currentMemberId,
    likedEventOnly: isMyPhotoCardsOnly
  });
  const { updateCollectedCount } = useInvalidateQueries({
    artistId: artistId || '',
    currentMember: currentMember || null
  });
  const hasMyPhotoCards = (data?.pages?.[0]?.meta?.photoCardsInfo?.collectedCount || 0) > 0;
  const { processedEvents } = useFilterPhotoCards({
    data: data || null,
    hasMyPhotoCards,
    isMyPhotoCardsOnly
  });

  // 첫 페이지가 필터링되어 비어 있을 때 자동으로 다음 페이지를 로드 (likedEventOnly 케이스 보호)
  useEffect(() => {
    const noCards =
      processedEvents.length === 0 || processedEvents.every((event) => isEmpty(event.photoCards));

    if (isMyPhotoCardsOnly && noCards && hasNextPage && !isLoading && !isFetchingNextPage) {
      loadMore();
    }
  }, [isMyPhotoCardsOnly, processedEvents, hasNextPage, isLoading, isFetchingNextPage, loadMore]);

  // 내 포토카드가 없는 사용자 오버레이 케이스에서도 첫 페이지가 비어 있으면 자동으로 다음 페이지 로드
  useEffect(() => {
    if (hasMyPhotoCards) return;
    const noOverlayCandidates = processedEvents.length === 0;
    if (noOverlayCandidates && hasNextPage && !isLoading && !isFetchingNextPage) {
      loadMore();
    }
  }, [hasMyPhotoCards, processedEvents, hasNextPage, isLoading, isFetchingNextPage, loadMore]);

  // 요약 정보
  const photoCardSummary = useMemo(() => {
    return data?.pages?.[0]?.meta?.photoCardsInfo || null;
  }, [data]);

  const onLongClickedPhotoCard = (photoCard: PhotoCardType) => {
    setSelectedPhotoCard(photoCard);
    setIsShowChangeCollectCountBottomSheet(true);
  };

  const onChangeArtist = (artist: ArtistType | null) => {
    if (!artist) {
      setIsHomeSelected(true);
      setSelectedArtist(null);
      return;
    }
    setIsHomeSelected(false);
    setSelectedArtist(artist);
    saveData('artist', artist.artistId);
    setCurrentMember(null);
    setForceRender(!forceRender);
  };

  const isShowFabButton = !isShowChangeCollectCountBottomSheet;

  //조건별 포토카드 메인 컨텐츠 리턴
  const renderPhotoCardContent = () => {
    if (effectiveArtistId && !currentMemberId) {
      return (
        <div className="px-[24px]">
          <EmptyPhotoCardEventSkeletonV2 variant="collection" />
        </div>
      );
    }

    if (!processedEvents.length && isLoading) {
      return (
        <div className="px-[24px]">
          <EmptyPhotoCardEventSkeletonV2 variant="collection" />
        </div>
      );
    }

    if (processedEvents.every((event) => isEmpty(event.photoCards)) && !hasNextPage) {
      // 모든 이벤트에 포토카드가 없고 더 이상 로드할 페이지가 없을 때
      return (
        <div className="w-full px-[24px] pb-[100px]">
          {/* @ts-expect-error */}
          <EmptyCase emoji={'🥺'} description={t('photoCardPage.emptyPhotoCards')} />
        </div>
      );
    }

    //포토카드가 있을때
    return (
      <div className="w-full h-full" style={{ position: 'relative' }}>
        <PhotoCardsByEventViewer
          events={processedEvents}
          onLongClickedPhotoCard={onLongClickedPhotoCard}
          endReached={loadMore}
          isNextPageLoading={isLoading || isFetchingNextPage}
        />
        {hasMyPhotoCards && <div className="mb-[30px]" />}
        {/* 내 포토카드가 없을 때 오버레이 */}
        {!hasMyPhotoCards && <EmptyMyPhotoCardContent />}
      </div>
    );
  };

  const renderBottomSection = () => {
    // 신고된 포토카드가 없을 때
    if (isEmpty(reportedPhotoCards)) {
      // 내 포토카드가 있다면 신고 박스 표시
      if (hasMyPhotoCards) {
        return (
          <div className="px-[24px] pt-[10px] pb-[205px]">
            <ReportPhotoCardBox artist={selectedArtist} member={currentMember} />
          </div>
        );
      }
      return null;
    }

    // 신고된 포토카드가 있을 때 대기 중인 포토카드 섹션 표시
    return (
      <div className="px-[24px] pt-[10px] pb-[200px]">
        <PendingPhotoCardsSection
          reportedPhotoCards={reportedPhotoCards}
          onViewAllClick={() => {
            navigate('/reported-photo-cards');
          }}
          onPhotoCardClick={(photoCard: PhotoCardType) => {
            openNewTab(`/photo_cards/${photoCard.photoCardId}`);
          }}
        />
      </div>
    );
  };

  return (
    <div className="pt-[env(safe-area-inset-top)] bg-scroll h-auto min-h-screen dark:bg-background-dark bg-background-light">
      <Helmet>
        <title>{t('tabs.collections')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <HomeHeader />
      {/* @ts-expect-error */}
      <ArtistIconSelector onSelected={onChangeArtist} showHomeOption showBinderCard />
      <hr className="h-[1px] w-full bg-separator1 border-none" />
      {isHomeSelected ? (
        <HomeView />
      ) : (
        <>
          <LikedMemberLists
            key={effectiveArtistId}
            artist={artist || null}
            onChangedMember={setCurrentMember}
            isMyPhotoCardsOnly={isMyPhotoCardsOnly}
            onChangePhotoCardFilter={() => {
              if (hasMyPhotoCards) setIsMyPhotoCardsOnly(!isMyPhotoCardsOnly);
            }}
          />
          <div>{renderPhotoCardContent()}</div>
          {!isFetchingNextPage && !isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              {renderBottomSection()}
            </motion.div>
          )}
          <CollectionSummaryBar
            totalCount={photoCardSummary?.totalCount || 0}
            collectedCount={photoCardSummary?.collectedCount || 0}
            currentMember={currentMember || null}
            showFabButton={isShowFabButton}
            selectedArtist={selectedArtist}
          />
        </>
      )}
      <HomeBottomNav tabIndex={0} className="![filter:none] border-t border-separator1" />

      {selectedPhotoCard && isShowChangeCollectCountBottomSheet && (
        <ChangePhotoCardCollectedCountBottomSheet
          isShowChangeCollectCountBottomSheet={isShowChangeCollectCountBottomSheet}
          setIsShowChangeCollectCountBottomSheet={setIsShowChangeCollectCountBottomSheet}
          currentCollectedCount={
            selectedPhotoCard.collectedCount > 0 ? selectedPhotoCard.collectedCount : 1
          }
          updatedCollectedCount={(collectedCount: number) =>
            updateCollectedCount(selectedPhotoCard, collectedCount)
          }
        />
      )}
    </div>
  );
}

const LikedMemberLists = ({
  artist,
  onChangedMember,
  isMyPhotoCardsOnly,
  onChangePhotoCardFilter
}: {
  artist: ArtistType | null;
  onChangedMember: (member: MemberType | null) => void;
  isMyPhotoCardsOnly: boolean;
  onChangePhotoCardFilter: () => void;
}) => {
  const { colorMode } = useColorMode();
  return (
    <div className="flex flex-row items-start px-[24px] pt-[10px] mb-[-4px]">
      <div className="flex-1 min-w-0">
        {/* @ts-expect-error */}
        <LikedMembersList
          artistMembers={artist?.members?.filter((member) => member.isLiked)}
          onChangeLikedMember={onChangedMember}
        />
      </div>
      <button
        type="button"
        onClick={onChangePhotoCardFilter}
        className={`        flex flex-row items-center gap-[4px] shrink-0 ml-[8px] mt-[8px] px-[12px] py-[7px] rounded-full border text-[14px] font-semibold ${
          isMyPhotoCardsOnly
            ? 'border-primary-light dark:border-primary-dark text-primary-light dark:text-primary-dark'
            : 'border-[rgba(0,0,0,0.08)] dark:border-[rgba(255,255,255,0.08)] text-main-light-1 dark:text-main-dark-1'
        }`}
      >
        {t('CollectionsHomePage.MyPhotoCardsOnly')}
        <Switch
          size="sm"
          isChecked={isMyPhotoCardsOnly}
          onChange={onChangePhotoCardFilter}
          colorScheme={colorMode === 'dark' ? 'gray' : 'purple'}
          pointerEvents="none"
          sx={{
            '&:not([data-checked]) .chakra-switch__track': {
              bg: `${colorMode === 'dark' ? '#303030' : '#CBCBCB'} !important`
            },
            ...(colorMode === 'dark' && {
              '& .chakra-switch__thumb': { background: 'white !important' }
            })
          }}
        />
      </button>
    </div>
  );
};
